import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const COLOR_NAMES = ['color', 'colour', 'cor', 'χρώμα', 'farbe', 'colore', 'couleur'];
const SIZE_NAMES = ['size', 'tamanho', 'μέγεθος', 'taille', 'taglia', 'größe'];

const FALLBACK_SWATCHES = {
  'μαύρος': '#1a1a1a',
  'μαύρο': '#1a1a1a',
  black: '#1a1a1a',
  'λευκό': '#f7f7f4',
  white: '#f7f7f4',
  'γκρι': '#8f918d',
  'γκρί': '#8f918d',
  gray: '#8f918d',
  grey: '#8f918d',
  'ανοιχτό γκρι': '#c2c3bf',
  'light gray': '#c2c3bf',
  'μεσονύχτιο γκρι': '#3a3d3c',
  'midnight gray': '#3a3d3c',
  'μπεζ': '#cdb494',
  beige: '#cdb494',
  'ανοιχτό μπεζ': '#e0d3bb',
  'light beige': '#e0d3bb',
  'άμμος': '#cdbb9e',
  sand: '#cdbb9e',
  'κρέμα': '#eee7d4',
  cream: '#eee7d4',
  'καστανός': '#6f4a32',
  brown: '#6f4a32',
  'σοκολάτα': '#4f3329',
  chocolate: '#4f3329',
  'κονιάκ': '#a9663a',
  cognac: '#a9663a',
  'τερακότα': '#b55a3c',
  terracotta: '#b55a3c',
  'πορτοκαλί': '#c46721',
  orange: '#c46721',
  'κίτρινος': '#d1ad42',
  yellow: '#d1ad42',
  'πράσινος': '#5f7f4d',
  green: '#5f7f4d',
  'σοφιστικέ πράσινο': '#97a585',
  'sage green': '#97a585',
  sage: '#97a585',
  'ελιά': '#65713e',
  olive: '#65713e',
  'μπλε': '#416a91',
  blue: '#416a91',
  'μπλε ναυτικό': '#1d2a44',
  navy: '#1d2a44',
  'navy blue': '#1d2a44',
  'μπλε της νύχτας': '#1c2436',
  'midnight blue': '#1c2436',
  'μπλε πετρόλ': '#2c5560',
  'petrol blue': '#2c5560',
  'γαλάζιο': '#a7c5db',
  'powder blue': '#a7c5db',
  'μωβ': '#6f5a86',
  purple: '#6f5a86',
};

const GREEK_COLOR_NAMES = {
  black: 'Μαύρο',
  white: 'Λευκό',
  gray: 'Γκρι',
  grey: 'Γκρι',
  'light gray': 'Ανοιχτό γκρι',
  'midnight gray': 'Σκούρο γκρι',
  beige: 'Μπεζ',
  'light beige': 'Ανοιχτό μπεζ',
  sand: 'Άμμος',
  cream: 'Κρεμ',
  brown: 'Καφέ',
  chocolate: 'Σοκολατί',
  cognac: 'Κονιάκ',
  terracotta: 'Τερακότα',
  orange: 'Πορτοκαλί',
  yellow: 'Κίτρινο',
  green: 'Πράσινο',
  'sage green': 'Πράσινο φασκόμηλου',
  sage: 'Πράσινο φασκόμηλου',
  olive: 'Λαδί',
  blue: 'Μπλε',
  navy: 'Σκούρο μπλε',
  'navy blue': 'Σκούρο μπλε',
  'midnight blue': 'Μπλε νύχτας',
  'petrol blue': 'Πετρόλ',
  'powder blue': 'Γαλάζιο',
  purple: 'Μωβ',
};

function normalize(value) {
  return String(value ?? '').trim().toLocaleLowerCase();
}

function optionMatches(name, candidates) {
  const normalized = normalize(name);
  return candidates.some((candidate) => normalized === candidate || normalized.includes(candidate));
}

function isColorOption(name) {
  return optionMatches(name, COLOR_NAMES);
}

function isSizeOption(name) {
  return optionMatches(name, SIZE_NAMES);
}

function getThemeRoute(routeName, fallback) {
  try {
    if (typeof Theme !== 'undefined' && Theme?.routes?.[routeName]) return Theme.routes[routeName];
  } catch (_) {
    // Theme is optional in preview/test environments.
  }

  return fallback;
}

function isLightColor(value) {
  const normalized = normalize(value);
  return ['white', 'λευκό', 'cream', 'κρέμα', 'light beige', 'ανοιχτό μπεζ'].includes(normalized);
}

class CompleteTheLook extends HTMLElement {
  #config;
  #items = [];
  #cartButton;
  #cartLabel;
  #cartTotal;
  #summary;
  #rewardStatus;
  #progressBar;
  #subtotal;
  #discount;
  #discountRow;
  #grandTotal;
  #globalError;
  #spinner;
  #slider;
  #slides = [];
  #dots = [];
  #carouselNav;
  #carouselCount;
  #activeSlide = 0;
  #carouselFrame = 0;
  #resizeObserver;
  #loading = false;
  #productDetails;

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.#config = this.#readJson(this.querySelector('[data-complete-look-config]')) ?? {
      moneyFormat: '{{amount}}',
      currency: 'EUR',
      discountPercent: 20,
      labels: {},
    };

    if (this.dataset.hideNativePurchase === 'true') {
      this.#productDetails = this.closest('.product-details');
      this.#productDetails?.classList.add('product-details--complete-look-primary');
    }

    this.#cartButton = this.querySelector('[data-complete-look-cart-button]');
    this.#cartLabel = this.querySelector('[data-complete-look-cart-label]');
    this.#cartTotal = this.querySelector('[data-complete-look-cart-total]');
    this.#summary = this.querySelector('[data-complete-look-summary]');
    this.#rewardStatus = this.querySelector('[data-complete-look-reward-status]');
    this.#progressBar = this.querySelector('[data-complete-look-progress-bar]');
    this.#subtotal = this.querySelector('[data-complete-look-subtotal]');
    this.#discount = this.querySelector('[data-complete-look-discount]');
    this.#discountRow = this.querySelector('[data-complete-look-discount-row]');
    this.#grandTotal = this.querySelector('[data-complete-look-grand-total]');
    this.#globalError = this.querySelector('[data-complete-look-global-error]');
    this.#spinner = this.querySelector('[data-complete-look-spinner]');

    this.querySelectorAll('[data-complete-look-item]').forEach((element) => this.#initializeItem(element));

    this.#cartButton?.addEventListener('click', () => this.#addSelectedItems());
    this.#initializeCarousel();
    this.#updateFooter();
  }

  disconnectedCallback() {
    if (this.#carouselFrame) cancelAnimationFrame(this.#carouselFrame);
    this.#resizeObserver?.disconnect();

    if (this.#productDetails) {
      const otherPrimaryBlocks = this.#productDetails.querySelectorAll(
        'complete-the-look[data-hide-native-purchase="true"]'
      );
      if (otherPrimaryBlocks.length <= 1) {
        this.#productDetails.classList.remove('product-details--complete-look-primary');
      }
    }
  }

  #readJson(element) {
    if (!(element instanceof HTMLScriptElement)) return null;

    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      console.error('[Complete the Look] Invalid JSON data.', error);
      return null;
    }
  }

  #initializeItem(element) {
    if (!(element instanceof HTMLElement)) return;

    const productData = this.#readJson(element.querySelector('[data-complete-look-product-json]'));
    if (!productData?.variants?.length) return;

    const firstAvailable = productData.variants.find((variant) => variant.available) ?? productData.variants[0];
    const hasAvailableVariant = productData.variants.some((variant) => variant.available);

    const item = {
      element,
      product: productData,
      selectedOptions: Array(productData.options.length).fill(null),
      variant: null,
      representative: firstAvailable,
      selected: this.dataset.selectAllDefault === 'true' && hasAvailableVariant,
      hasAvailableVariant,
      optionsRoot: element.querySelector('[data-complete-look-options]'),
      price: element.querySelector('[data-complete-look-price]'),
      comparePrice: element.querySelector('[data-complete-look-compare-price]'),
      image: element.querySelector('[data-complete-look-image], .complete-look__image'),
      toggles: [...element.querySelectorAll('[data-complete-look-toggle]')],
      toggleLabels: [...element.querySelectorAll('[data-complete-look-toggle-label]')],
      error: element.querySelector('[data-complete-look-item-error]'),
      controls: [],
    };

    productData.options.forEach((optionName, optionIndex) => {
      const values = this.#optionValues(item, optionIndex);

      if (values.length === 1) {
        item.selectedOptions[optionIndex] = values[0];
      } else if (isColorOption(optionName)) {
        item.selectedOptions[optionIndex] = firstAvailable.options[optionIndex] ?? values[0] ?? null;
      }
    });

    this.#renderOptions(item);
    this.#refreshItem(item, { updateImage: true });

    item.toggles.forEach((toggle) => {
      toggle.addEventListener('click', () => {
        if (!item.hasAvailableVariant || this.#loading) return;

        item.selected = !item.selected;
        this.#clearItemError(item);
        this.#clearGlobalError();
        this.#renderSelectionState(item);
        this.#updateFooter();
      });
    });

    this.#items.push(item);
  }

  #optionValues(item, optionIndex) {
    const configuredValues = item.product.optionValues?.[optionIndex]?.values?.map((value) => value.name) ?? [];
    if (configuredValues.length) return configuredValues;

    return [...new Set(item.product.variants.map((variant) => variant.options[optionIndex]).filter(Boolean))];
  }

  #displayOptionName(optionName) {
    if (isColorOption(optionName)) return this.#config.labels.colorOption || 'ΧΡΩΜΑ';
    if (isSizeOption(optionName)) return this.#config.labels.sizeOption || 'ΜΕΓΕΘΟΣ';
    return optionName;
  }

  #displayOptionValue(optionName, value) {
    if (!value) return '';
    if (!isColorOption(optionName)) return value;
    return GREEK_COLOR_NAMES[normalize(value)] || value;
  }

  #renderOptions(item) {
    if (!(item.optionsRoot instanceof HTMLElement)) return;
    item.optionsRoot.replaceChildren();
    item.controls = [];

    item.product.options.forEach((optionName, optionIndex) => {
      const values = this.#optionValues(item, optionIndex);
      if (values.length <= 1) return;

      const displayName = this.#displayOptionName(optionName);
      const option = document.createElement('div');
      option.className = 'complete-look__option';
      option.dataset.optionIndex = String(optionIndex);

      const header = document.createElement('div');
      header.className = 'complete-look__option-header';

      const name = document.createElement('p');
      name.className = 'complete-look__option-name';
      name.textContent = displayName;

      const selectedValue = document.createElement('p');
      selectedValue.className = 'complete-look__option-value';
      selectedValue.dataset.selectedOptionValue = '';

      header.append(name, selectedValue);

      const valuesRoot = document.createElement('div');
      valuesRoot.className = 'complete-look__values';

      const controls = [];

      values.forEach((value) => {
        const button = document.createElement('button');
        const colorOption = isColorOption(optionName);

        button.type = 'button';
        button.className = colorOption
          ? 'complete-look__option-button complete-look__swatch'
          : 'complete-look__option-button';
        button.dataset.optionIndex = String(optionIndex);
        button.dataset.optionValue = value;
        button.setAttribute('aria-label', `${displayName}: ${this.#displayOptionValue(optionName, value)}`);
        button.setAttribute('aria-pressed', 'false');

        if (colorOption) {
          const swatch = this.#getSwatch(item, optionIndex, value);
          const nativeColor = typeof swatch?.swatchColor === 'string' ? swatch.swatchColor : '';
          const color = nativeColor || FALLBACK_SWATCHES[normalize(value)] || '#d8d6d0';

          button.style.setProperty('--swatch-color', color);
          if (swatch?.swatchImage) button.style.setProperty('--swatch-image', `url("${swatch.swatchImage}")`);
          if (isLightColor(value)) button.classList.add('is-light');
        } else {
          button.textContent = value;
        }

        button.addEventListener('click', () => this.#selectOption(item, optionIndex, value));
        valuesRoot.append(button);
        controls.push(button);
      });

      option.append(header, valuesRoot);
      item.optionsRoot.append(option);
      item.controls.push({ optionIndex, optionName, displayName, selectedValue, buttons: controls });
    });
  }

  #getSwatch(item, optionIndex, value) {
    return item.product.optionValues?.[optionIndex]?.values?.find((entry) => entry.name === value) ?? null;
  }

  #selectOption(item, optionIndex, value) {
    if (!item.selected || this.#loading) return;

    item.selectedOptions[optionIndex] = value;
    this.#normalizeSelection(item, optionIndex);
    this.#clearItemError(item);
    this.#clearGlobalError();
    this.#refreshItem(item, { updateImage: isColorOption(item.product.options[optionIndex]) });
    this.#updateFooter();
  }

  #normalizeSelection(item, changedIndex) {
    item.selectedOptions.forEach((selectedValue, optionIndex) => {
      if (optionIndex === changedIndex || selectedValue == null) return;

      const possible = item.product.variants.some((variant) => {
        if (!variant.available) return false;
        if (variant.options[optionIndex] !== selectedValue) return false;

        return item.selectedOptions.every((candidate, index) => {
          if (candidate == null || index === optionIndex) return true;
          return variant.options[index] === candidate;
        });
      });

      if (!possible) item.selectedOptions[optionIndex] = null;
    });
  }

  #refreshItem(item, { updateImage = false } = {}) {
    item.variant = this.#findExactVariant(item);
    item.representative = item.variant ?? this.#findRepresentativeVariant(item);

    this.#updateControls(item);

    const allOptionsSelected = item.selectedOptions.every((value) => value != null);
    const unavailable = allOptionsSelected && !item.variant?.available;

    if (item.error) {
      item.error.textContent = unavailable ? this.#config.labels.unavailable ?? 'Μη διαθέσιμο' : '';
      item.error.hidden = !unavailable;
    }

    this.#updatePrice(item, item.representative);
    if (updateImage) this.#updateImage(item, item.representative);
    this.#renderSelectionState(item);
  }

  #findExactVariant(item) {
    if (item.selectedOptions.some((value) => value == null)) return null;

    return (
      item.product.variants.find(
        (variant) => variant.options.every((value, index) => value === item.selectedOptions[index])
      ) ?? null
    );
  }

  #findRepresentativeVariant(item) {
    const matching = item.product.variants.filter((variant) =>
      item.selectedOptions.every((value, index) => value == null || variant.options[index] === value)
    );

    return matching.find((variant) => variant.available) ?? matching[0] ?? item.product.variants[0] ?? null;
  }

  #updateControls(item) {
    item.controls.forEach((control) => {
      const selected = item.selectedOptions[control.optionIndex];
      control.selectedValue.textContent = selected
        ? this.#displayOptionValue(control.optionName, selected)
        : this.#config.labels.selectOption ?? 'Επίλεξε';

      control.buttons.forEach((button) => {
        const value = button.dataset.optionValue;
        const active = selected === value;
        const possible = item.product.variants.some((variant) => {
          if (!variant.available || variant.options[control.optionIndex] !== value) return false;

          return item.selectedOptions.every((candidate, index) => {
            if (candidate == null || index === control.optionIndex) return true;
            return variant.options[index] === candidate;
          });
        });

        button.disabled = !item.selected || !possible || this.#loading;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    });
  }

  #updatePrice(item, variant) {
    if (!variant) return;

    if (item.price) item.price.textContent = this.#formatMoney(variant.price);

    if (item.comparePrice) {
      const onSale = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);
      item.comparePrice.textContent = onSale ? this.#formatMoney(variant.compareAtPrice) : '';
      item.comparePrice.classList.toggle('hidden', !onSale);
    }
  }

  #updateImage(item, variant) {
    if (!(item.image instanceof HTMLImageElement) || !variant?.image || item.image.src === variant.image) return;

    item.image.style.opacity = '0';
    const restore = () => {
      item.image.style.opacity = '1';
      item.image.removeEventListener('load', restore);
      item.image.removeEventListener('error', restore);
      this.#measureCarousel();
    };

    item.image.addEventListener('load', restore);
    item.image.addEventListener('error', restore);
    item.image.removeAttribute('srcset');
    item.image.removeAttribute('sizes');
    item.image.src = variant.image;

    if (item.image.complete) requestAnimationFrame(restore);
  }

  #renderSelectionState(item) {
    if (!item.hasAvailableVariant) item.selected = false;

    item.element.classList.toggle('is-selected', item.selected);
    item.element.classList.toggle('is-unavailable', !item.hasAvailableVariant);

    item.toggles.forEach((toggle) => {
      toggle.disabled = !item.hasAvailableVariant || this.#loading;
      toggle.setAttribute('aria-pressed', item.selected ? 'true' : 'false');
      toggle.setAttribute(
        'aria-label',
        `${item.selected ? this.#config.labels.added : this.#config.labels.add} — ${item.product.title}`
      );
    });

    item.toggleLabels.forEach((label) => {
      label.textContent = item.selected
        ? this.#config.labels.added ?? 'ΣΤΟ ΣΥΝΟΛΟ'
        : this.#config.labels.add ?? 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΣΥΝΟΛΟ';
    });

    this.#updateControls(item);
    this.#measureCarousel();
  }

  #selectedItems() {
    return this.#items.filter((item) => item.selected && item.hasAvailableVariant);
  }

  #getMissingSelection(item) {
    if (!item.selected) return null;

    const missingIndex = item.selectedOptions.findIndex((value) => value == null);
    if (missingIndex >= 0) {
      return {
        item,
        optionName: this.#displayOptionName(item.product.options[missingIndex]),
      };
    }

    if (!item.variant?.available) {
      return {
        item,
        optionName: this.#config.labels.unavailable ?? 'Μη διαθέσιμο',
      };
    }

    return null;
  }

  #replaceTokens(template, replacements) {
    return Object.entries(replacements).reduce(
      (result, [token, value]) => result.replaceAll(`[${token}]`, String(value)),
      String(template ?? '')
    );
  }

  #updateFooter() {
    const selectedItems = this.#selectedItems();
    const selectedCount = selectedItems.length;
    const totalCount = this.#items.length;
    const allSelected = totalCount > 0 && selectedCount === totalCount;
    const discountPercent = Number(this.#config.discountPercent ?? this.dataset.discountPercent ?? 20);
    const subtotal = selectedItems.reduce(
      (sum, item) => sum + Number((item.variant ?? item.representative)?.price || 0),
      0
    );
    const discountAmount = allSelected ? Math.round((subtotal * discountPercent) / 100) : 0;
    const finalTotal = Math.max(0, subtotal - discountAmount);
    const missing = selectedItems.map((item) => this.#getMissingSelection(item)).find(Boolean) ?? null;

    if (this.#summary) {
      this.#summary.textContent = this.#replaceTokens(this.#config.labels.selectedCount ?? '[selected] / [total]', {
        selected: selectedCount,
        total: totalCount,
      });
    }

    if (this.#rewardStatus) {
      if (allSelected) {
        this.#rewardStatus.textContent = this.#replaceTokens(
          this.#config.labels.fullDiscount ?? 'Η ΕΚΠΤΩΣΗ [discount]% ΕΝΕΡΓΟΠΟΙΗΘΗΚΕ',
          { discount: discountPercent }
        );
        this.#rewardStatus.classList.add('is-active');
      } else if (selectedCount > 0) {
        this.#rewardStatus.textContent = this.#replaceTokens(
          this.#config.labels.addMore ?? 'Πρόσθεσε ακόμη [count] για έκπτωση [discount]%.',
          { count: totalCount - selectedCount, discount: discountPercent }
        );
        this.#rewardStatus.classList.remove('is-active');
      } else {
        this.#rewardStatus.textContent = this.#config.labels.nothingSelected ?? 'Επίλεξε τα κομμάτια που θέλεις.';
        this.#rewardStatus.classList.remove('is-active');
      }
    }

    if (this.#progressBar) {
      const progress = totalCount > 0 ? (selectedCount / totalCount) * 100 : 0;
      this.#progressBar.style.width = `${Math.max(0, Math.min(100, progress))}%`;
      this.#progressBar.classList.toggle('is-complete', allSelected);
    }

    if (this.#subtotal) this.#subtotal.textContent = selectedCount > 0 ? this.#formatMoney(subtotal) : '—';

    if (this.#discount) {
      this.#discount.textContent = allSelected
        ? `−${this.#formatMoney(discountAmount)}`
        : this.#config.labels.noDiscount ?? '—';
    }

    this.#discountRow?.classList.toggle('is-active', allSelected);

    if (this.#grandTotal) {
      this.#grandTotal.textContent = selectedCount > 0 ? this.#formatMoney(finalTotal) : '—';
    }

    let buttonLabel = this.#config.labels.emptyButton ?? 'ΕΠΙΛΕΞΕ ΤΟΥΛΑΧΙΣΤΟΝ 1 ΠΡΟΪΟΝ';
    let buttonDisabled = selectedCount === 0 || Boolean(missing) || this.#loading;

    if (missing) {
      buttonLabel = this.#replaceTokens(
        this.#config.labels.requiredOption ?? 'Επίλεξε [option] για το [product].',
        {
          option: missing.optionName,
          product: missing.item.product.title,
        }
      );
    } else if (selectedCount > 0) {
      if (allSelected) {
        buttonLabel = this.#config.labels.cartFull ?? 'ΠΡΟΣΘΗΚΗ ΣΥΝΟΛΟΥ';
      } else if (selectedCount === 1) {
        buttonLabel = this.#config.labels.cartSingle ?? 'ΠΡΟΣΘΗΚΗ 1 ΠΡΟΪΟΝΤΟΣ';
      } else {
        buttonLabel = this.#replaceTokens(this.#config.labels.cartMultiple ?? 'ΠΡΟΣΘΗΚΗ [count] ΠΡΟΪΟΝΤΩΝ', {
          count: selectedCount,
        });
      }
    }

    if (this.#cartLabel && !this.#loading) this.#cartLabel.textContent = buttonLabel;

    if (this.#cartTotal) {
      this.#cartTotal.textContent = selectedCount > 0 ? this.#formatMoney(finalTotal) : '';
      this.#cartTotal.hidden = selectedCount === 0;
    }

    if (this.#cartButton) this.#cartButton.disabled = buttonDisabled;
  }

  #formatMoney(value) {
    return formatMoney(Number(value || 0), this.#config.moneyFormat, this.#config.currency);
  }

  async #addSelectedItems() {
    const selectedItems = this.#selectedItems();
    if (!selectedItems.length || !this.#cartButton || this.#loading) return;

    const missing = selectedItems.map((item) => this.#getMissingSelection(item)).find(Boolean) ?? null;
    if (missing) {
      this.#showMissingSelection(missing);
      return;
    }

    const totalCount = this.#items.length;
    const fullLook = selectedItems.length === totalCount;
    const discountPercent = Number(this.#config.discountPercent ?? this.dataset.discountPercent ?? 20);
    const bundleKey = `complete-look:${this.dataset.currentProductId || 'product'}`;

    const items = selectedItems.map((item) => ({
      id: Number(item.variant.id),
      quantity: 1,
      properties: {
        _complete_look_bundle: bundleKey,
        _complete_look_source: String(this.dataset.currentProductId || ''),
        _complete_look_complete: fullLook ? 'true' : 'false',
        _complete_look_discount: fullLook ? `${discountPercent}%` : '0%',
      },
    }));

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const sections = this.#getCartSectionIds();
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.#setLoading(true);
    this.#clearGlobalError();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(getThemeRoute('cart_add_url', '/cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.#config.labels.error);
      }

      const cart = await this.#fetchCart();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'complete-the-look',
          sourceId: this.id,
          itemCount: totalQuantity,
          productId: this.dataset.currentProductId,
          sections: result.sections,
          didError: false,
        },
      });

      if (this.dataset.openCartDrawer === 'true') this.#openCartDrawer();
    } catch (error) {
      const message = error instanceof Error ? error.message : this.#config.labels.error;

      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: message || this.#config.labels.error,
          code: 'INVALID',
        })
      );
      this.#showGlobalError(message || this.#config.labels.error);
    } finally {
      this.#setLoading(false);
    }
  }

  #showMissingSelection(missing) {
    const message = this.#replaceTokens(
      this.#config.labels.requiredOption ?? 'Επίλεξε [option] για το [product].',
      {
        option: missing.optionName,
        product: missing.item.product.title,
      }
    );

    if (missing.item.error) {
      missing.item.error.textContent = message;
      missing.item.error.hidden = false;
    }

    missing.item.element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
  }

  #openCartDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer || drawer.isOpen) return;

    customElements.whenDefined('theme-drawer').then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!drawer.isOpen && typeof drawer.open === 'function') drawer.open();
        });
      });
    });
  }

  #getCartSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) {
        sectionIds.add(component.dataset.sectionId);
      }
    });

    return [...sectionIds];
  }

  async #fetchCart() {
    const cartUrl = getThemeRoute('cart_url', '/cart');
    const response = await fetch(`${cartUrl}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(this.#config.labels.error);
    return response.json();
  }

  #setLoading(loading) {
    this.#loading = loading;
    this.classList.toggle('is-loading', loading);

    this.#items.forEach((item) => this.#renderSelectionState(item));

    if (this.#cartButton) {
      this.#cartButton.disabled = loading;
      this.#cartButton.classList.toggle('is-loading', loading);
      this.#cartButton.setAttribute('aria-busy', loading ? 'true' : 'false');
    }

    this.#spinner?.toggleAttribute('hidden', !loading);

    if (this.#cartLabel && loading) {
      this.#cartLabel.textContent = this.#config.labels.loading ?? 'ΠΡΟΣΘΗΚΗ...';
    }

    if (!loading) this.#updateFooter();
  }

  #initializeCarousel() {
    this.#slider = this.querySelector('[data-complete-look-slider]');
    if (!(this.#slider instanceof HTMLElement)) return;

    this.#slides = [...this.#slider.querySelectorAll('[data-complete-look-item]')];
    this.#dots = [...this.querySelectorAll('[data-complete-look-dot]')];
    this.#carouselNav = this.querySelector('[data-complete-look-carousel-nav]');
    this.#carouselCount = this.querySelector('[data-complete-look-carousel-count]');

    this.#slider.addEventListener(
      'scroll',
      () => {
        if (this.#carouselFrame) cancelAnimationFrame(this.#carouselFrame);
        this.#carouselFrame = requestAnimationFrame(() => this.#syncActiveSlideFromScroll());
      },
      { passive: true }
    );

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#setActiveSlide(index, { scroll: true }));
    });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#measureCarousel());
      this.#resizeObserver.observe(this.#slider);
      this.#slides.forEach((slide) => this.#resizeObserver.observe(slide));
    }

    requestAnimationFrame(() => {
      this.#setActiveSlide(0);
      this.#measureCarousel();
    });
  }

  #syncActiveSlideFromScroll() {
    if (!this.#slider || !this.#slides.length) return;

    const sliderRect = this.#slider.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    this.#slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - sliderRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.#setActiveSlide(closestIndex);
  }

  #setActiveSlide(index, { scroll = false } = {}) {
    if (!this.#slides.length) return;

    this.#activeSlide = Math.max(0, Math.min(index, this.#slides.length - 1));

    this.#slides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active-slide', slideIndex === this.#activeSlide);
    });

    this.#dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.#activeSlide;
      dot.classList.toggle('is-active', active);
      dot.toggleAttribute('aria-current', active);
    });

    if (this.#carouselCount) {
      this.#carouselCount.textContent = `${this.#activeSlide + 1} / ${this.#slides.length}`;
    }

    if (scroll && this.#slider) {
      const slide = this.#slides[this.#activeSlide];
      this.#slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }

    this.#measureCarousel();
  }

  #measureCarousel() {
    if (!this.#slider || !this.#slides.length) return;

    requestAnimationFrame(() => {
      if (!this.#slider) return;

      const overflow = this.#slider.scrollWidth > this.#slider.clientWidth + 4;
      this.#slider.classList.toggle('is-carousel', overflow);

      if (this.#carouselNav instanceof HTMLElement) this.#carouselNav.hidden = !overflow;

      if (overflow) {
        const activeSlide = this.#slides[this.#activeSlide] ?? this.#slides[0];
        this.#slider.style.setProperty('--complete-look-slider-height', `${activeSlide.offsetHeight + 2}px`);
      } else {
        this.#slider.style.removeProperty('--complete-look-slider-height');
      }
    });
  }

  #clearItemError(item) {
    if (!item.error) return;
    item.error.hidden = true;
    item.error.textContent = '';
  }

  #showGlobalError(message) {
    if (!this.#globalError) return;
    this.#globalError.textContent = message;
    this.#globalError.hidden = false;
  }

  #clearGlobalError() {
    if (!this.#globalError) return;
    this.#globalError.hidden = true;
    this.#globalError.textContent = '';
  }
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}
